/**
 * Groups adjacent implied (auto) time entries for cleaner UI.
 * Firestore rows stay as-is; this is display-only.
 */

import type { LeadTimeEntry } from '../types';

/** Merge implied segments when gap between end → next start is at most this (ms). */
export const LEAD_TIME_MERGE_GAP_MS = 5 * 60 * 1000;

export type LeadTimeDisplayGroup =
  | { kind: 'single'; entry: LeadTimeEntry }
  | { kind: 'merged'; entries: LeadTimeEntry[] };

/**
 * Build display groups: consecutive implied entries (no note) with small gaps are merged.
 * Explicit entries and implied-with-note stay as single rows.
 */
export function buildLeadTimeDisplayGroups(entries: LeadTimeEntry[]): LeadTimeDisplayGroup[] {
  const implied = entries.filter(e => e.type === 'implied' && !e.note?.trim());
  const other = entries.filter(e => e.type !== 'implied' || !!e.note?.trim());

  const sorted = [...implied].sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
  const chunks: LeadTimeEntry[][] = [];

  for (const e of sorted) {
    const prev = chunks[chunks.length - 1];
    if (prev) {
      const last = prev[prev.length - 1];
      const gap = e.startTime.getTime() - last.endTime.getTime();
      if (gap <= LEAD_TIME_MERGE_GAP_MS) {
        prev.push(e);
        continue;
      }
    }
    chunks.push([e]);
  }

  const groups: LeadTimeDisplayGroup[] = [];
  for (const arr of chunks) {
    groups.push(arr.length === 1 ? { kind: 'single', entry: arr[0] } : { kind: 'merged', entries: arr });
  }
  for (const e of other) {
    groups.push({ kind: 'single', entry: e });
  }

  groups.sort((a, b) => {
    const endA = a.kind === 'single' ? a.entry.endTime : a.entries[a.entries.length - 1].endTime;
    const endB = b.kind === 'single' ? b.entry.endTime : b.entries[b.entries.length - 1].endTime;
    return endB.getTime() - endA.getTime();
  });

  return groups;
}

export function sumMinutesInGroup(group: LeadTimeDisplayGroup): number {
  if (group.kind === 'single') return group.entry.minutes;
  return group.entries.reduce((s, e) => s + e.minutes, 0);
}
